import { Building2, CheckCircle2, Zap } from "lucide-react";
import PropTypes from "prop-types";
import { ICON_MAP } from "../constants/themes";
import EditableText from "./Builder/EditableText";

// ── Helper: background class based on bg_style ─────────────────
function getBgClass(bgStyle, index, compact) {
	if (compact) {
		const alt = index % 2 === 0 ? "bg-[#0F121C]" : "";
		switch (bgStyle) {
			case "dark":
				return "bg-slate-900/80";
			case "accent":
				return "bg-gradient-to-br from-cyan-500/10 to-purple-600/10";
			case "gradient":
				return "bg-gradient-to-r from-[#0F121C] via-[#131620] to-[#0F121C]";
			default:
				return alt;
		}
	}
	const alt = index % 2 === 0 ? "bg-[#0F121C] border-y border-white/5" : "";
	switch (bgStyle) {
		case "dark":
			return "bg-slate-900/80 border-y border-white/5";
		case "accent":
			return "bg-gradient-to-br from-cyan-500/10 to-purple-600/10 border-y border-cyan-500/10";
		case "gradient":
			return "bg-gradient-to-r from-[#0F121C] via-[#131620] to-[#0F121C] border-y border-white/5";
		default:
			return alt;
	}
}

// ── Helper: padding class ──────────────────────────────────────
function getPadClass(padding, compact) {
	if (compact) {
		switch (padding) {
			case "sm":
				return "p-3";
			case "lg":
				return "p-6";
			default:
				return "p-5";
		}
	}
	switch (padding) {
		case "sm":
			return "py-10";
		case "lg":
			return "py-24";
		default:
			return "py-16";
	}
}

// ── Helper: text alignment class ───────────────────────────────
function getAlignClass(textAlign) {
	switch (textAlign) {
		case "center":
			return "text-center";
		case "right":
			return "text-right";
		default:
			return "text-left";
	}
}

// ── Badge element builder ──────────────────────────────────────
function Badge({ icon: Icon, text, compact, color = "purple" }) {
	if (!text) return null;
	const colors = {
		purple: "bg-purple-500/10 border-purple-500/20 text-purple-400",
		cyan: "bg-cyan-500/10 border-cyan-500/20 text-cyan-400",
	};
	if (compact) {
		return (
			<div
				className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wide mb-2 ${colors[color] || colors.purple}`}
			>
				<Icon size={10} />
				{text}
			</div>
		);
	}
	return (
		<div
			className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs font-bold uppercase tracking-wide mb-6 ${colors[color] || colors.purple}`}
		>
			<Icon size={14} />
			{text}
		</div>
	);
}

Badge.propTypes = {
	icon: PropTypes.elementType.isRequired,
	text: PropTypes.string,
	compact: PropTypes.bool,
	color: PropTypes.oneOf(["purple", "cyan"]),
};

// ═══════════════════════════════════════════════════════════════
// Company Intro Section
// ═══════════════════════════════════════════════════════════════
function CompanyIntroSection({
	section,
	company,
	compact,
	bgClass: _bgClass,
	padClass,
	alignClass,
	onUpdate,
	editable,
}) {
	if (!company.description && !company.tagline) return null;

	if (compact) {
		return (
			<div className={`${padClass} ${_bgClass} border-b border-white/5`}>
				<Badge
					icon={Building2}
					text={section.badge || "Sobre a Empresa"}
					compact
				/>
				<h4 className={`text-sm font-bold text-white mb-2 ${alignClass}`}>
					{section.title || "Quem Somos"}
				</h4>
				{company.tagline && (
					<p className={`text-xs text-cyan-400 font-medium mb-1 ${alignClass}`}>
						{company.tagline}
					</p>
				)}
				{company.description && (
					<p
						className={`text-xs text-slate-400 leading-relaxed line-clamp-3 ${alignClass}`}
					>
						{company.description}
					</p>
				)}
			</div>
		);
	}

	return (
		<section className={`${padClass} ${_bgClass} relative overflow-hidden`}>
			<div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-purple-500/5 rounded-full blur-[150px] -z-10"></div>
			<div
				className={`max-w-6xl mx-auto px-6 ${alignClass === "text-center" ? "" : ""}`}
			>
				<div
					className={
						alignClass === "text-center"
							? "max-w-4xl mx-auto text-center"
							: "max-w-3xl"
					}
				>
					<Badge icon={Building2} text={section.badge || "Sobre a Empresa"} />
					<EditableText
						tagName="h2"
						value={section.title || "Quem Somos"}
						onChange={(val) => onUpdate?.("title", val)}
						editable={editable}
						className={`text-3xl font-bold text-white mb-6 ${alignClass}`}
					/>
					{company.tagline && (
						<p
							className={`text-xl text-cyan-400 font-medium mb-4 ${alignClass}`}
						>
							{company.tagline}
						</p>
					)}
					{company.description && (
						<p
							className={`text-slate-400 text-lg leading-relaxed whitespace-pre-line ${alignClass}`}
						>
							{company.description}
						</p>
					)}
				</div>
			</div>
		</section>
	);
}

CompanyIntroSection.propTypes = {
	section: PropTypes.shape({
		title: PropTypes.string,
		badge: PropTypes.string,
	}).isRequired,
	company: PropTypes.shape({
		name: PropTypes.string,
		tagline: PropTypes.string,
		description: PropTypes.string,
	}).isRequired,
	compact: PropTypes.bool,
	bgClass: PropTypes.string,
	padClass: PropTypes.string,
	alignClass: PropTypes.string,
	onUpdate: PropTypes.func,
	editable: PropTypes.bool,
};

// ═══════════════════════════════════════════════════════════════
// Custom (Text) Section – supports layouts
// ═══════════════════════════════════════════════════════════════
// eslint-disable-next-line complexity
function CustomSection({
	section,
	compact,
	bgClass,
	padClass,
	alignClass,
	onUpdate,
	editable,
}) {
	const SectionIcon = ICON_MAP[section.icon] || Zap;
	const layout = section.layout || "full-width";

	if (compact) {
		if (layout === "two-column") {
			return (
				<div className={`${padClass} ${bgClass} border-b border-white/5`}>
					<div className="grid grid-cols-2 gap-4">
						<div className={alignClass}>
							<Badge icon={SectionIcon} text={section.badge} compact />
							<h4 className="text-sm font-bold text-white mb-1">
								{section.title}
							</h4>
							<p className="text-xs text-slate-400 leading-relaxed line-clamp-3">
								{section.content}
							</p>
						</div>
						{section.image_url && (
							<div className="rounded-lg overflow-hidden border border-white/5 bg-slate-800/50">
								<img
									src={section.image_url}
									alt=""
									className="w-full h-full object-cover"
								/>
							</div>
						)}
					</div>
				</div>
			);
		}
		if (layout === "card-highlight") {
			return (
				<div className={`${padClass} ${bgClass} border-b border-white/5`}>
					<div className="bg-gradient-to-br from-cyan-500/5 to-purple-500/5 border border-cyan-500/10 rounded-xl p-4">
						<div className="flex items-start gap-3">
							<div className="w-8 h-8 rounded-lg bg-cyan-500/10 flex items-center justify-center flex-shrink-0">
								<SectionIcon className="text-cyan-400" size={16} />
							</div>
							<div className={alignClass}>
								<h4 className="text-sm font-bold text-white mb-1">
									{section.title}
								</h4>
								<p className="text-xs text-slate-400 leading-relaxed line-clamp-3">
									{section.content}
								</p>
							</div>
						</div>
					</div>
				</div>
			);
		}
		// centered and full-width
		return (
			<div
				className={`${padClass} ${bgClass} border-b border-white/5 ${layout === "centered" ? "text-center" : alignClass}`}
			>
				<Badge icon={SectionIcon} text={section.badge} compact />
				<h4 className="text-sm font-bold text-white mb-1">{section.title}</h4>
				<p className="text-xs text-slate-400 leading-relaxed line-clamp-3">
					{section.content}
				</p>
			</div>
		);
	}

	// Full render
	const badgeEl = <Badge icon={SectionIcon} text={section.badge} />;

	const TitleEl = (
		<EditableText
			tagName="h2"
			value={section.title}
			onChange={(val) => onUpdate?.("title", val)}
			editable={editable}
			className={`text-3xl font-bold text-white mb-6 ${alignClass !== "text-left" && !["two-column", "card-highlight"].includes(layout) ? "text-center" : ""}`}
		/>
	);

	const ContentEl = (
		<EditableText
			tagName="p"
			value={section.content}
			onChange={(val) => onUpdate?.("content", val)}
			editable={editable}
			className={`text-slate-400 text-lg leading-relaxed whitespace-pre-line ${alignClass !== "text-left" && !["two-column", "card-highlight"].includes(layout) ? "text-center" : ""}`}
		/>
	);

	if (layout === "two-column") {
		return (
			<section className={`${padClass} ${bgClass} relative`}>
				<div className={`max-w-6xl mx-auto px-6`}>
					<div className="grid lg:grid-cols-2 gap-12 items-center">
						<div className={alignClass}>
							{badgeEl}
							{TitleEl}
							{ContentEl}
						</div>
						{section.image_url && (
							<div className="rounded-2xl overflow-hidden border border-white/5">
								<img
									src={section.image_url}
									alt={section.title}
									className="w-full h-auto object-cover"
								/>
							</div>
						)}
					</div>
				</div>
			</section>
		);
	}

	if (layout === "centered") {
		return (
			<section className={`${padClass} ${bgClass} relative`}>
				<div className="max-w-4xl mx-auto px-6 text-center">
					{badgeEl}
					{TitleEl}
					{ContentEl}
				</div>
			</section>
		);
	}

	if (layout === "card-highlight") {
		return (
			<section className={`${padClass} ${bgClass} relative`}>
				<div className="max-w-4xl mx-auto px-6">
					<div className="bg-gradient-to-br from-cyan-500/5 to-purple-500/5 border border-cyan-500/10 rounded-2xl p-10">
						<div className="flex items-start gap-6">
							<div className="w-14 h-14 rounded-xl bg-cyan-500/10 flex items-center justify-center flex-shrink-0">
								<SectionIcon className="text-cyan-400" size={28} />
							</div>
							<div className={alignClass}>
								{badgeEl}
								<EditableText
									tagName="h2"
									value={section.title}
									onChange={(val) => onUpdate?.("title", val)}
									editable={editable}
									className="text-3xl font-bold text-white mb-4"
								/>
								<EditableText
									tagName="p"
									value={section.content}
									onChange={(val) => onUpdate?.("content", val)}
									editable={editable}
									className="text-slate-400 text-lg leading-relaxed whitespace-pre-line"
								/>
							</div>
						</div>
					</div>
				</div>
			</section>
		);
	}

	// Default: full-width
	return (
		<section className={`${padClass} ${bgClass} relative`}>
			<div className={`max-w-6xl mx-auto px-6`}>
				<div
					className={
						alignClass === "text-center"
							? "max-w-4xl mx-auto text-center"
							: "max-w-3xl"
					}
				>
					{badgeEl}
					{TitleEl}
					{ContentEl}
				</div>
			</div>
		</section>
	);
}

CustomSection.propTypes = {
	section: PropTypes.shape({
		title: PropTypes.string,
		content: PropTypes.string,
		badge: PropTypes.string,
		icon: PropTypes.string,
		layout: PropTypes.string,
		image_url: PropTypes.string,
	}).isRequired,
	compact: PropTypes.bool,
	bgClass: PropTypes.string,
	padClass: PropTypes.string,
	alignClass: PropTypes.string,
	onUpdate: PropTypes.func,
	editable: PropTypes.bool,
};

// ═══════════════════════════════════════════════════════════════
// Features Grid Section (NEW)
// ═══════════════════════════════════════════════════════════════
function FeaturesGridSection({
	section,
	compact,
	bgClass,
	padClass,
	alignClass,
	onUpdate,
	editable,
}) {
	const SectionIcon = ICON_MAP[section.icon] || Zap;
	const cards = section.cards || [];
	const columns = section.columns || 3;

	if (compact) {
		return (
			<div className={`${padClass} ${bgClass} border-b border-white/5`}>
				<Badge icon={SectionIcon} text={section.badge} compact />
				<h4 className={`text-sm font-bold text-white mb-2 ${alignClass}`}>
					{section.title}
				</h4>
				{cards.length > 0 && (
					<div
						className={`grid ${columns >= 3 ? "grid-cols-3" : "grid-cols-2"} gap-2`}
					>
						{cards.slice(0, 6).map((card, ci) => {
							const CardIcon = ICON_MAP[card.icon] || Zap;
							return (
								<div
									key={`card-${ci}`}
									className="bg-slate-800/50 border border-white/5 rounded-lg p-2"
								>
									<CardIcon size={12} className="text-cyan-400 mb-1" />
									<p className="text-[10px] font-bold text-white leading-tight">
										{card.title}
									</p>
								</div>
							);
						})}
					</div>
				)}
				{cards.length === 0 && (
					<p className="text-[10px] text-slate-500 italic">
						Nenhum card adicionado
					</p>
				)}
			</div>
		);
	}

	const colClass =
		columns === 2
			? "sm:grid-cols-2"
			: columns === 4
				? "sm:grid-cols-2 lg:grid-cols-4"
				: "sm:grid-cols-2 lg:grid-cols-3";

	return (
		<section className={`${padClass} ${bgClass} relative overflow-hidden`}>
			<div className="max-w-6xl mx-auto px-6">
				<div className={`mb-12 ${alignClass}`}>
					<Badge icon={SectionIcon} text={section.badge} />
					<EditableText
						tagName="h2"
						value={section.title}
						onChange={(val) => onUpdate?.("title", val)}
						editable={editable}
						className="text-3xl font-bold text-white mb-4"
					/>
				</div>
				{cards.length > 0 ? (
					<div className={`grid ${colClass} gap-6`}>
						{cards.map((card, ci) => {
							const CardIcon = ICON_MAP[card.icon] || CheckCircle2;
							return (
								<div
									key={`card-${ci}`}
									className="bg-[#151926]/80 backdrop-blur-sm border border-white/5 rounded-2xl p-6 hover:border-cyan-500/20 transition-all group"
								>
									<div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
										<CardIcon className="text-cyan-400" size={24} />
									</div>
									<h3 className="text-lg font-bold text-white mb-2">
										{card.title}
									</h3>
									{card.content && (
										<p className="text-sm text-slate-400 leading-relaxed">
											{card.content}
										</p>
									)}
								</div>
							);
						})}
					</div>
				) : (
					<p className="text-slate-500 italic text-center">
						Nenhum card adicionado
					</p>
				)}
			</div>
		</section>
	);
}

FeaturesGridSection.propTypes = {
	section: PropTypes.shape({
		title: PropTypes.string,
		badge: PropTypes.string,
		icon: PropTypes.string,
		columns: PropTypes.number,
		cards: PropTypes.arrayOf(
			PropTypes.shape({
				icon: PropTypes.string,
				title: PropTypes.string,
				content: PropTypes.string,
			}),
		),
	}).isRequired,
	compact: PropTypes.bool,
	bgClass: PropTypes.string,
	padClass: PropTypes.string,
	alignClass: PropTypes.string,
	onUpdate: PropTypes.func,
	editable: PropTypes.bool,
};

// ═══════════════════════════════════════════════════════════════
// Image Gallery Section (NEW)
// ═══════════════════════════════════════════════════════════════
function ImageGallerySection({
	section,
	compact,
	bgClass,
	padClass,
	alignClass,
	onUpdate,
	editable,
}) {
	const SectionIcon = ICON_MAP[section.icon] || Zap;
	const images = section.images || [];
	const columns = section.columns || 2;

	if (compact) {
		return (
			<div className={`${padClass} ${bgClass} border-b border-white/5`}>
				<Badge icon={SectionIcon} text={section.badge} compact />
				<h4 className={`text-sm font-bold text-white mb-2 ${alignClass}`}>
					{section.title}
				</h4>
				{images.length > 0 ? (
					<div className="grid grid-cols-2 gap-2">
						{images.slice(0, 4).map((img, ii) => (
							<div
								key={`img-${ii}`}
								className="rounded-lg overflow-hidden border border-white/5 bg-slate-800/50 aspect-video"
							>
								<img
									src={img.url}
									alt={img.caption || ""}
									className="w-full h-full object-cover"
								/>
							</div>
						))}
					</div>
				) : (
					<p className="text-[10px] text-slate-500 italic">
						Nenhuma imagem adicionada
					</p>
				)}
			</div>
		);
	}

	const colClass =
		columns === 1
			? "max-w-2xl mx-auto"
			: columns === 3
				? "grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
				: "grid sm:grid-cols-2 gap-6";

	return (
		<section className={`${padClass} ${bgClass} relative overflow-hidden`}>
			<div className="max-w-6xl mx-auto px-6">
				<div className={`mb-12 ${alignClass}`}>
					<Badge icon={SectionIcon} text={section.badge} />
					<EditableText
						tagName="h2"
						value={section.title}
						onChange={(val) => onUpdate?.("title", val)}
						editable={editable}
						className="text-3xl font-bold text-white mb-4"
					/>
				</div>
				{images.length > 0 ? (
					<div className={colClass}>
						{images.map((img, ii) => (
							<div
								key={`img-${ii}`}
								className={`group ${columns === 1 ? "mb-6" : ""}`}
							>
								<div className="rounded-2xl overflow-hidden border border-white/5 bg-slate-800/50">
									<img
										src={img.url}
										alt={img.caption || ""}
										className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-300"
									/>
								</div>
								{img.caption && (
									<p className="text-sm text-slate-400 mt-2 text-center">
										{img.caption}
									</p>
								)}
							</div>
						))}
					</div>
				) : (
					<p className="text-slate-500 italic text-center">
						Nenhuma imagem adicionada
					</p>
				)}
			</div>
		</section>
	);
}

ImageGallerySection.propTypes = {
	section: PropTypes.shape({
		title: PropTypes.string,
		badge: PropTypes.string,
		icon: PropTypes.string,
		columns: PropTypes.number,
		images: PropTypes.arrayOf(
			PropTypes.shape({
				url: PropTypes.string,
				caption: PropTypes.string,
			}),
		),
	}).isRequired,
	compact: PropTypes.bool,
	bgClass: PropTypes.string,
	padClass: PropTypes.string,
	alignClass: PropTypes.string,
	onUpdate: PropTypes.func,
	editable: PropTypes.bool,
};

// ═══════════════════════════════════════════════════════════════
// CTA / Destaque Section (NEW)
// ═══════════════════════════════════════════════════════════════
function CtaSection({
	section,
	compact,
	bgClass: _bgClass,
	padClass,
	alignClass,
	onUpdate,
	editable,
}) {
	const SectionIcon = ICON_MAP[section.icon] || Zap;

	if (compact) {
		return (
			<div
				className={`${padClass} border-b border-white/5 bg-gradient-to-br from-cyan-500/10 to-purple-600/10`}
			>
				<div className={alignClass}>
					<Badge icon={SectionIcon} text={section.badge} compact color="cyan" />
					<h4 className="text-sm font-bold text-white mb-1">{section.title}</h4>
					{section.content && (
						<p className="text-xs text-slate-300 leading-relaxed line-clamp-2">
							{section.content}
						</p>
					)}
				</div>
			</div>
		);
	}

	return (
		<section
			className={`${padClass} bg-gradient-to-br from-cyan-500/10 via-[#0F121C] to-purple-600/10 border-y border-cyan-500/10 relative overflow-hidden`}
		>
			<div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-cyan-500/5 rounded-full blur-[150px] -z-10"></div>
			<div
				className={`max-w-4xl mx-auto px-6 ${alignClass === "text-left" ? "" : alignClass}`}
			>
				<Badge icon={SectionIcon} text={section.badge} color="cyan" />
				<EditableText
					tagName="h2"
					value={section.title}
					onChange={(val) => onUpdate?.("title", val)}
					editable={editable}
					className={`text-4xl md:text-5xl font-bold text-white mb-6 leading-tight ${alignClass}`}
				/>
				{section.content && (
					<EditableText
						tagName="p"
						value={section.content}
						onChange={(val) => onUpdate?.("content", val)}
						editable={editable}
						className={`text-lg text-slate-300 leading-relaxed whitespace-pre-line max-w-2xl ${alignClass === "text-center" ? "mx-auto" : ""} ${alignClass}`}
					/>
				)}
			</div>
		</section>
	);
}

CtaSection.propTypes = {
	section: PropTypes.shape({
		title: PropTypes.string,
		content: PropTypes.string,
		badge: PropTypes.string,
		icon: PropTypes.string,
	}).isRequired,
	compact: PropTypes.bool,
	bgClass: PropTypes.string,
	padClass: PropTypes.string,
	alignClass: PropTypes.string,
	onUpdate: PropTypes.func,
	editable: PropTypes.bool,
};

// ═══════════════════════════════════════════════════════════════
// Main SectionRenderer
// ═══════════════════════════════════════════════════════════════
/**
 * Renders a single proposal section based on its type.
 *
 * @param {Object} props
 * @param {Object} props.section - Section data object
 * @param {number} props.index - Section index (for alternating bg)
 * @param {Object} [props.company] - { name, tagline, description } for company_intro
 * @param {boolean} [props.compact=false] - Compact mode for preview
 * @param {function} [props.onUpdate] - Callback for editing (field, value) => {}
 * @param {boolean} [props.editable=false] - Whether to enable editing mode
 */
export default function SectionRenderer({
	section,
	index,
	company = {},
	compact = false,
	onUpdate,
	editable = false,
}) {
	const bgClass = getBgClass(section.bg_style, index, compact);
	const padClass = getPadClass(section.padding, compact);
	const alignClass = getAlignClass(section.text_align);

	const shared = {
		section,
		compact,
		bgClass,
		padClass,
		alignClass,
		onUpdate,
		editable,
	};

	switch (section.type) {
		case "company_intro":
			return <CompanyIntroSection {...shared} company={company} />;
		case "features_grid":
			return <FeaturesGridSection {...shared} />;
		case "image_gallery":
			return <ImageGallerySection {...shared} />;
		case "cta":
			return <CtaSection {...shared} />;
		default:
			return <CustomSection {...shared} />;
	}
}

SectionRenderer.propTypes = {
	section: PropTypes.shape({
		type: PropTypes.string.isRequired,
		bg_style: PropTypes.string,
		padding: PropTypes.string,
		text_align: PropTypes.string,
	}).isRequired,
	index: PropTypes.number.isRequired,
	company: PropTypes.shape({
		name: PropTypes.string,
		tagline: PropTypes.string,
		description: PropTypes.string,
	}),
	compact: PropTypes.bool,
	onUpdate: PropTypes.func,
	editable: PropTypes.bool,
};
